<template>
    <div class="phone-mockup">
        <div class="phone-header">
            <span class="carrier">{{ carrier }}</span>
            <span class="time">{{ time }}</span>
            <span class="battery">{{ battery }}</span>
        </div>

        <div class="screen-wrapper">
            <transition
                name="phone-slide"
                mode="out-in"
            >
                <div
                    v-if="current_mode === 'messages'"
                    key="messages"
                    class="screen-content"
                >
                    <div
                        class="message-header"
                        v-if="show_message_header"
                    >
                        <h4>{{ business_name }}</h4>
                        <span class="phone-number">{{ phone_number }}</span>
                    </div>

                    <div
                        class="message-content"
                        data-scroll-container
                    >
                        <div
                            v-if="show_typing_indicator"
                            class="typing-indicator"
                            key="typing"
                        >
                            <div class="typing-dots">
                                <span></span>
                                <span></span>
                                <span></span>
                            </div>
                        </div>

                        <div
                            class="message-container"
                            :class="{ 'messages-visible': show_messages }"
                        >
                            <slot name="messages"></slot>
                        </div>
                    </div>
                </div>

                <div
                    v-else-if="current_mode === 'browser'"
                    key="browser"
                    class="screen-content"
                >
                    <div class="browser-header">
                        <div class="browser-controls">

                            <div class="address-bar">
                                <v-icon
                                    size="14"
                                    class="lock-icon"
                                >mdi-lock</v-icon>
                                <span class="url">{{ browser_url }}</span>
                            </div>
                        </div>
                    </div>

                    <div
                        class="browser-content"
                        data-scroll-container
                    >
                        <transition
                            name="browser-content-slide"
                            mode="out-in"
                        >
                            <div :key="browser_content_key">
                                <slot name="browser"></slot>
                            </div>
                        </transition>
                    </div>
                </div>
            </transition>
        </div>
    </div>
</template>

<script>
export default {
    name: 'MockPhone',

    props: {
        carrier: {
            type: String,
            default: 'Verizon'
        },

        time: {
            type: String,
            default: '2:34 PM'
        },

        battery: {
            type: String,
            default: '87%'
        },

        business_name: {
            type: String,
            default: 'Business Name'
        },

        phone_number: {
            type: String,
            default: '(555) 123-4567'
        },

        show_message_header: {
            type: Boolean,
            default: true
        },

        auto_animate: {
            type: Boolean,
            default: true
        },

        animation_delay: {
            type: Number,
            default: 200
        },

        typing_duration: {
            type: Number,
            default: 1500
        },

        infinite_replay: {
            type: Boolean,
            default: false
        },

        replay_delay: {
            type: Number,
            default: 8000
        },

        mode: {
            type: String,
            default: 'messages',
            validator: (value) => ['messages', 'browser'].includes(value)
        },

        browser_url: {
            type: String,
            default: 'barrelcreek.com'
        },

        browser_content_key: {
            type: [String, Number],
            default: 'default'
        }
    },

    data() {
        return {
            show_typing_indicator: false,
            show_messages: false,
            replay_timeout: null,
            current_mode: 'messages',
            isScrolling: false,
            scrollStartY: 0,
            scrollStartTop: 0,
            eventListeners: []
        }
    },


    methods: {
        startAnimation() {
            setTimeout(() => {
                this.show_typing_indicator = true

                setTimeout(() => {
                    this.show_typing_indicator = false
                    this.show_messages = true

                    if (this.infinite_replay) {
                        this.replay_timeout = setTimeout(() => {
                            this.resetAnimation()
                        }, this.replay_delay)
                    }
                }, this.typing_duration)
            }, this.animation_delay)
        },

        resetAnimation() {
            this.show_typing_indicator = false
            this.show_messages = false

            if (this.replay_timeout) {
                clearTimeout(this.replay_timeout)
                this.replay_timeout = null
            }

            if (this.auto_animate) {
                this.startAnimation()
            }
        },

        // mimic touch scrolling behavior
        setupTouchScrolling() {
            this.cleanupEventListeners();

            this.$nextTick(() => {
                setTimeout(() => {
                    if (!this.$el) return;

                    const scrollContainers = this.$el.querySelectorAll('[data-scroll-container]');

                    scrollContainers.forEach(container => {
                        const isMessageContainer = container.classList.contains('message-content');
                        const isBrowserContainer = container.classList.contains('browser-content');

                        if ((isMessageContainer && this.current_mode !== 'messages') ||
                            (isBrowserContainer && this.current_mode !== 'browser')) {
                            return;
                        }

                        // prevent text selection globally on the container
                        container.style.webkitUserSelect = 'none';
                        container.style.mozUserSelect = 'none';
                        container.style.msUserSelect = 'none';
                        container.style.userSelect = 'none';

                        let isDragging = false;
                        let startY = 0;
                        let startScrollTop = 0;
                        let hasMoved = false;

                        const handleMouseDown = (e) => {
                            // don't interfere with actual form interactions
                            if (e.target.closest('.v-input, .v-btn, a, button, input, textarea, select')) {
                                return;
                            }

                            isDragging = true;
                            hasMoved = false;
                            startY = e.clientY;
                            startScrollTop = container.scrollTop;
                            container.style.cursor = 'grabbing';

                            e.preventDefault();
                            e.stopPropagation();
                        };

                        const handleMouseMove = (e) => {
                            if (!isDragging) return;

                            hasMoved = true;
                            const deltaY = startY - e.clientY;
                            const newScrollTop = startScrollTop + deltaY;

                            container.scrollTop = newScrollTop;

                            e.preventDefault();
                            e.stopPropagation();
                        };

                        const handleMouseUp = (e) => {
                            if (isDragging) {
                                isDragging = false;
                                container.style.cursor = 'grab';

                                if (hasMoved) {
                                    e.preventDefault();
                                    e.stopPropagation();
                                }
                            }
                        };

                        // prevent context menu on long press
                        const handleContextMenu = (e) => {
                            if (hasMoved) {
                                e.preventDefault();
                            }
                        };

                        let touchStartY = 0;
                        let touchStartScrollTop = 0;

                        const handleTouchStart = (e) => {
                            if (e.target.closest('.v-input, .v-btn, a, button, input, textarea, select')) {
                                return;
                            }
                            touchStartY = e.touches[0].clientY;
                            touchStartScrollTop = container.scrollTop;
                        };

                        const handleTouchMove = (e) => {
                            const deltaY = touchStartY - e.touches[0].clientY;
                            container.scrollTop = touchStartScrollTop + deltaY;
                        };

                        container.addEventListener('mousedown', handleMouseDown);
                        document.addEventListener('mousemove', handleMouseMove);
                        document.addEventListener('mouseup', handleMouseUp);
                        container.addEventListener('contextmenu', handleContextMenu);
                        container.addEventListener('touchstart', handleTouchStart, { passive: false });
                        container.addEventListener('touchmove', handleTouchMove, { passive: true });

                        this.eventListeners.push({
                            element: container,
                            event: 'mousedown',
                            handler: handleMouseDown
                        });
                        this.eventListeners.push({
                            element: document,
                            event: 'mousemove',
                            handler: handleMouseMove
                        });
                        this.eventListeners.push({
                            element: document,
                            event: 'mouseup',
                            handler: handleMouseUp
                        });
                        this.eventListeners.push({
                            element: container,
                            event: 'contextmenu',
                            handler: handleContextMenu
                        });
                        this.eventListeners.push({
                            element: container,
                            event: 'touchstart',
                            handler: handleTouchStart
                        });
                        this.eventListeners.push({
                            element: container,
                            event: 'touchmove',
                            handler: handleTouchMove
                        });
                    });
                }, 100); // small additional delay to ensure transition elements are rendered
            });
        },

        cleanupEventListeners() {
            this.eventListeners.forEach(({ element, event, handler }) => {
                element.removeEventListener(event, handler);
            });
            this.eventListeners = [];
        }
    },

    mounted() {
        this.current_mode = this.mode;
        if (this.auto_animate && this.mode === 'messages') {
            this.startAnimation()
        } else if (this.mode === 'messages') {
            this.show_messages = true
        }

        this.setupTouchScrolling();
    },

    watch: {
        mode: {
            handler(newMode) {
                this.current_mode = newMode;
                this.$nextTick(this.setupTouchScrolling);
            },
            immediate: true
        }
    },

    updated() {
        this.$nextTick(() => {
            const browserContent = this.$el.querySelector('.browser-content');
            if (browserContent && this.current_mode === 'browser') {
                browserContent.scrollTop = 0;
            }
            this.setupTouchScrolling();
        });
    },

    beforeUnmount() {
        if (this.replay_timeout) {
            clearTimeout(this.replay_timeout);
        }
        this.cleanupEventListeners();
    },

}
</script>

<style lang="scss" scoped>
.phone {
    &-mockup {
        max-width: 390px;
        width: min(390px, 100%);
        height: 720px;
        margin: 0 auto;
        background: #1a1a1a;
        border-radius: 2.5rem;
        padding: 0.5rem;
        box-shadow:
            0 0 0 2px #333,
            0 8px 32px rgba(0, 0, 0, 0.4),
            inset 0 1px 0 rgba(255, 255, 255, 0.1);
        position: relative;

        // island
        &::before {
            content: '';
            position: absolute;
            top: 0.75rem;
            left: 50%;
            transform: translateX(-50%);
            width: 120px;
            height: 25px;
            background: #000;
            border-radius: 15px;
            z-index: 10;
        }

        // home indicator
        &::after {
            content: '';
            position: absolute;
            bottom: 0.5rem;
            left: 50%;
            transform: translateX(-50%);
            width: 140px;
            height: 4px;
            background: rgba(255, 255, 255, 0.3);
            border-radius: 2px;
        }
    }

    &-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        color: white;
        font-size: 0.8rem;
        font-weight: 600;
        padding: 2rem 1rem 0.5rem;
        position: relative;
        z-index: 5;

        .carrier {
            font-weight: 500;
        }

        .time {
            position: absolute;
            left: 50%;
            transform: translateX(-50%);
            font-weight: 600;
        }

        .battery {
            display: flex;
            align-items: center;
            position: relative;

            &::after {
                content: '';
                display: inline-block;
                width: 22px;
                height: 11px;
                border: 1px solid white;
                border-radius: 2px;
                margin-left: 4px;
                position: relative;

                &::before {
                    content: '';
                    position: absolute;
                    right: -3px;
                    top: 3px;
                    width: 2px;
                    height: 5px;
                    background: white;
                    border-radius: 0 1px 1px 0;
                }
            }

            &::before {
                content: '';
                position: absolute;
                right: 6px;
                top: 50%;
                transform: translateY(-50%);
                width: 16px;
                height: 10px;
                background: white;
                border-radius: 1px;
            }
        }
    }
}

.screen {
    &-wrapper {
        background: white;
        border-radius: 1.2rem;
        height: 620px;
        margin: 0 0.5rem;
        display: flex;
        flex-direction: column;
        overflow: hidden;
        position: relative;
    }

    &-content {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        display: flex;
        flex-direction: column;
        overflow: hidden;
    }
}

.message {
    &-header {
        text-align: center;
        border-bottom: 1px solid #eee;
        padding-top: 0.75rem;
        padding-bottom: 0.5rem;
        margin-bottom: 1rem;
        flex-shrink: 0;

        h4 {
            margin: 0;
            font-size: 0.95rem;
            font-weight: 600;
            line-height: 1;
            color: #333;
        }

        .phone-number {
            color: #666;
            font-size: 0.8rem;
        }
    }

    &-content {
        flex: 1;
        padding: 0 1rem;
        overflow-y: auto;
        position: relative;
        -webkit-overflow-scrolling: touch;
        touch-action: pan-y;
        overscroll-behavior: contain;
        cursor: grab;
        -webkit-user-select: none;
        -moz-user-select: none;
        -ms-user-select: none;
        user-select: none;

        &:active {
            cursor: grabbing;
        }

        .v-input,
        .v-btn,
        a,
        button,
        input,
        textarea,
        select {
            -webkit-user-select: text;
            -moz-user-select: text;
            -ms-user-select: text;
            user-select: text;
            cursor: auto;
        }

        &::-webkit-scrollbar {
            width: 3px;
            background: transparent;
        }

        &::-webkit-scrollbar-thumb {
            background: rgba(0, 0, 0, 0.2);
            border-radius: 3px;
            transition: background 0.2s ease;
        }

        &::-webkit-scrollbar-thumb:hover {
            background: rgba(0, 0, 0, 0.4);
        }

        &::-webkit-scrollbar-track {
            background: transparent;
        }

        scrollbar-width: thin;
        scrollbar-color: rgba(0, 0, 0, 0.2) transparent;
    }

    &-container {
        opacity: 0;
        transform: translateY(10px);
        transition: all 0.2s ease-out;

        &.messages-visible {
            opacity: 1;
            transform: translateY(0);
        }
    }
}

.typing {
    &-indicator {
        display: flex;
        align-items: center;
        margin-bottom: 0.5rem;
        animation: fadeIn 0.25s ease-in;
    }

    &-dots {
        background: #e5e5ea;
        border-radius: 18px;
        padding: 0.8rem 1rem;
        display: flex;
        align-items: center;
        gap: 0.3rem;

        span {
            width: 6px;
            height: 6px;
            border-radius: 50%;
            background: #999;
            animation: typingDot 0.6s infinite ease-in-out;

            &:nth-child(1) {
                animation-delay: 0s;
            }

            &:nth-child(2) {
                animation-delay: 0.2s;
            }

            &:nth-child(3) {
                animation-delay: 0.4s;
            }
        }
    }
}

.browser {
    &-header {
        flex-shrink: 0;
        border-bottom: 1px solid #e0e0e0;
        padding: 0.5rem;

        .browser-controls {
            display: flex;
            align-items: center;
            gap: 0.375rem;

            .address-bar {
                position: relative;
                flex: 1;
                min-width: 0;
                background: #f8f8f8;
                border-radius: 16px;
                padding: 0.375rem 0.625rem;
                display: flex;
                align-items: center;
                gap: 0.375rem;

                .lock-icon {
                    position: absolute;
                    top: 50%;
                    left: 0.5rem;
                    transform: translateY(-50%);
                    color: #666;
                    flex-shrink: 0;
                }

                .url {
                    font-size: 0.75rem;
                    color: #333;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                    min-width: 0;
                    flex: 1;
                    text-align: center;
                    padding: 0 1rem;
                }
            }
        }
    }

    &-content {
        flex: 1;
        padding: .5rem;
        overflow-y: auto;
        overflow-x: hidden;
        background: white;
        touch-action: pan-y;
        overscroll-behavior: contain;
        cursor: grab;
        -webkit-user-select: none;
        -moz-user-select: none;
        -ms-user-select: none;
        user-select: none;

        &:active {
            cursor: grabbing;
        }

        .v-input,
        .v-btn,
        a,
        button,
        input,
        textarea,
        select {
            -webkit-user-select: text;
            -moz-user-select: text;
            -ms-user-select: text;
            user-select: text;
            cursor: auto;
        }

        &::-webkit-scrollbar {
            width: 3px;
            background: transparent;
        }

        &::-webkit-scrollbar-thumb {
            background: rgba(0, 0, 0, 0.2);
            border-radius: 3px;
            transition: background 0.2s ease;
        }

        &::-webkit-scrollbar-thumb:hover {
            background: rgba(0, 0, 0, 0.4);
        }

        &::-webkit-scrollbar-track {
            background: transparent;
        }

        scrollbar-width: thin;
        scrollbar-color: rgba(0, 0, 0, 0.2) transparent;
    }
}

// mobile-sized inputs
:deep(.mobile-form-container) {
    width: 100%;
    max-width: 100%;
    padding: 0.75rem;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    font-size: 14px;
    line-height: 1.4;

    .form-title {
        font-size: 1.1rem;
        font-weight: 600;
        margin: 0 0 0.5rem 0;
        text-align: center;
        color: #333;
    }

    .v-btn {
        text-transform: none;
        letter-spacing: normal;

        &.v-btn--outlined {
            border-width: 1.5px;
        }
    }

    .v-icon {
        &.v-icon--left {
            margin-right: 0.375rem;
        }
    }

    .mobile-actions {
        display: flex;
        flex-direction: column;
        gap: 0.75rem;
        margin-bottom: 1rem;

        .action-btn {
            font-size: 0.85rem;
            font-weight: 500;
            height: 44px;
            border-radius: 0.5rem;

            &.submit-btn {
                font-weight: 600;
            }
        }
    }

    .mobile-checkbox {
        margin: 0;
        padding: 0;

        .v-input--selection-controls__input {
            margin-right: 2px;
            align-self: flex-start;
        }

        .v-input--selection-controls__ripple {
            width: 32px;
            height: 32px;
        }

        .checkbox-label {
            font-size: 0.75rem;
            line-height: 1.3;
            color: #555;
        }
    }

    .mobile-input {
        margin-bottom: 0.75rem;

        &:last-of-type {
            margin-bottom: 0;
        }

        .v-input__control {
            min-height: 44px;
        }

        .v-text-field__details {
            margin-top: 0.25rem;
            margin-bottom: 0;
            padding-top: 0;
        }

        .v-input__slot {
            padding: 0 0.75rem;
            font-size: 0.85rem;
        }

        .v-label {
            font-size: 0.8rem;
        }

        .v-text-field__slot input {
            font-size: 0.85rem;
        }
    }

    .v-textarea {
        .v-input__control {
            min-height: auto;
        }

        .v-input__slot {
            padding: 0.75rem;
        }

        textarea {
            font-size: 0.85rem;
            line-height: 1.4;
            font-family: inherit;
        }

        .v-label {
            top: 0.75rem;
            left: 0.75rem;
        }

        .v-label-active {
            top: 0.25rem;
            font-size: 0.75rem;
        }
    }

    .v-input__details {
        min-height: auto;
        padding-top: 0.25rem;
    }

    .v-text-field--outlined fieldset {
        border-color: #dee2e6;
    }

    .v-input--checkbox .v-input__control {
        min-height: auto;
    }

    .v-input--checkbox .v-input__slot {
        margin-bottom: 0;
    }

    .v-input--selection-controls.v-input--dense .v-input--selection-controls__ripple {
        left: -11px;
    }
}

// animations
@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(10px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@keyframes typingDot {

    0%,
    60%,
    100% {
        transform: scale(1);
        opacity: 0.5;
    }

    30% {
        transform: scale(1.2);
        opacity: 1;
    }
}

.phone-slide-enter-active,
.phone-slide-leave-active {
    transition: all 0.4s ease-in-out;
}

.phone-slide-enter-from {
    opacity: 0;
    transform: translateX(100%);
}

.phone-slide-leave-to {
    opacity: 0;
    transform: translateX(-100%);
}

.phone-slide-enter-to,
.phone-slide-leave-from {
    opacity: 1;
    transform: translateX(0);
}

.browser-content-slide-enter-active,
.browser-content-slide-leave-active {
    transition: all 0.35s ease-out;
}

.browser-content-slide-enter-from {
    opacity: 0.2;
    transform: translateX(100%);
}

.browser-content-slide-leave-to {
    opacity: 0.2;
    transform: translateX(-100%);
}

.browser-content-slide-enter-to,
.browser-content-slide-leave-from {
    opacity: 1;
    transform: translateX(0);
}
</style>
